import { NgTemplateOutlet } from '@angular/common';
import { afterRenderEffect, Component, DestroyRef, effect, ElementRef, inject, input, output, signal, TemplateRef, viewChild, viewChildren } from '@angular/core';
import { PnycBadgeDirective } from '@ui-kit/directives/pnyc-badge/pnyc-badge.directive';
import { PnycIconComponent } from '@ui-kit/pnyc-icon/pnyc-icon.component';

export interface NavigationPanelItem {
    id: string;
    icon: string;
    name: string;
    avatarUrl?: string;
    avatarFullName?: string;
    badge?: number;
    template?: TemplateRef<void>;
}

@Component({
    selector: 'pnyc-navigation-panel',
    templateUrl: './pnyc-navigation-panel.component.html',
    styleUrls: ['./pnyc-navigation-panel.component.scss'],
    imports: [
        PnycIconComponent,
        PnycBadgeDirective,
        NgTemplateOutlet
    ]
})
export class PnycNavigationPanelComponent {
    private readonly destroyRef = inject(DestroyRef);

    public readonly items = input.required<NavigationPanelItem[]>();
    public readonly badgeLimit = input<number>(99);

    public readonly clicked = output<NavigationPanelItem>();

    private readonly bubble = viewChild<ElementRef<HTMLElement>>('bubble');
    private readonly slots = viewChildren<ElementRef<HTMLElement>>('slot');
    private readonly navContainer = viewChild<ElementRef<HTMLElement>>('navContainer');

    public readonly activeItemId = signal<string | null>(null);
    private readonly resizeTick = signal(0);

    constructor() {
        effect(() => {
            const navContainerEl = this.navContainer()?.nativeElement;

            if (!navContainerEl) {
                return;
            }

            const observer = new ResizeObserver(() => this.resizeTick.update(v => v + 1));

            observer.observe(navContainerEl);

            this.destroyRef.onDestroy(() => observer.disconnect());
        });

        afterRenderEffect({
            earlyRead: () => {
                this.resizeTick();

                const slotsArr = this.slots();
                const idx = this.items().findIndex(i => i.id === this.activeItemId());

                if (slotsArr.length === 0 || idx < 0) {
                    return null;
                }

                const el = slotsArr[idx]?.nativeElement;

                if (!el) {
                    return null;
                }

                return {
                    left: el.offsetLeft,
                    width: el.offsetWidth
                };
            },
            write: (dimensions) => {
                const bubbleEl = this.bubble()?.nativeElement;

                if (!bubbleEl) {
                    return;
                }

                if (!dimensions()) {
                    bubbleEl.style.opacity = '0';

                    return;
                }

                bubbleEl.style.opacity = '1';
                bubbleEl.style.left = dimensions()!.left + 'px';
                bubbleEl.style.width = dimensions()!.width + 'px';
            }
        });
    }

    public onItemClicked(item: NavigationPanelItem): void {
        this.clicked.emit(item);
    }

    public setActiveItem(id: string | null): void {
        this.activeItemId.set(id);
    }
}
